import type { WhereFilterOp } from "firebase/firestore";
import { collection, query, where, getDocs } from "firebase/firestore";
import { firestore } from "../../../services/firebase";

// Tipos
export type Filter = {
  col: string;
  op: string;
  value: string | number | boolean; // <-- Allow more types
  logic?: string;
  orWith?: Filter;
};

// Filtro local com suporte a AND/OR
export function applyLocalFilters<T extends Record<string, unknown>>(baseData: T[], filters: Filter[]): T[] {
  if (!filters.length) return baseData;
  let filtered = baseData;
  filters.forEach(f => {
    if (f.logic === "OR" && f.orWith) {
      const set1 = baseData.filter(row => applyFilterToRow(row, f));
      const set2 = baseData.filter(row => applyFilterToRow(row, f.orWith!));
      filtered = Array.from(new Set([...set1, ...set2]));
    } else {
      filtered = filtered.filter(row => applyFilterToRow(row, f));
    }
  });
  return filtered;
}

export function applyFilterToRow<T extends Record<string, unknown>>(row: T, f: Filter): boolean {
  const value = row[f.col];
  if (f.op === "==") return String(value) === String(f.value);
  if (f.op === "!=") return String(value) !== String(f.value);
  if (f.op === ">") return Number(value) > Number(f.value);
  if (f.op === "<") return Number(value) < Number(f.value);
  if (f.op === "contains") return String(value).toLowerCase().includes(String(f.value).toLowerCase());
  return true;
}

// Contagem de valores por coluna
export function getValueCounts<T extends Record<string, unknown>>(data: T[], columns: string[]): Record<string, Record<string, number>> {
  const result: Record<string, Record<string, number>> = {};
  columns.forEach(col => {
    result[col] = {};
    data.forEach(row => {
      const val = row[col];
      if (val !== undefined && val !== null) {
        result[col][String(val)] = (result[col][String(val)] || 0) + 1;
      }
    });
  });
  return result;
}

// Filtro remoto (Firestore)
export async function handleMultiFilter<T extends Record<string, unknown>>(
  filters: Filter[],
  collectionName: string,
  setOriginalData: (data: T[]) => void,
  onDataChange: (data: T[]) => void
) {
  if (!collectionName || !firestore) return;
  const ref = collection(firestore, collectionName);
  let q: import("firebase/firestore").Query = ref;
  filters.forEach((f) => {
    if (f.op === "contains") {
      q = query(q, where(f.col, ">=", f.value), where(f.col, "<=", f.value + "\uf8ff"));
    } else if (f.op !== "!=") {
      q = query(q, where(f.col, f.op as WhereFilterOp, f.value));
    }
  });
  const snap = await getDocs(q);
  const docs = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  setOriginalData(docs as unknown as T[]);
  onDataChange(docs as unknown as T[]);
}

// Suporte a filtro remoto
export function supportsRemoteFiltering(filters: Filter[]) {
  return filters.every(f => f.op !== "!=" && f.op !== "contains" && (!("logic" in f) || f.logic === undefined));
}